import { useEffect, useRef, useState } from 'react';
import { Play, Bookmark as BookmarkIcon } from 'react-feather';
import { Video, WatchState } from '../../../types/Video';
import {
  incrementVideoView,
  updateVideoScheduling,
} from '../../../services/videos';
import posthog from 'posthog-js';
import { hapticsImpactLight } from '../../../utils/haptics';
import { enqueueSnackbar } from 'notistack';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import {
  addVideoBookmark,
  isVideoBookmarked,
  removeVideoBookmark,
} from '../../../services/bookmarks';
import { Tooltip } from '@mui/material';

const VideoPlayer: React.FC<{
  title: string;
  cover: string;
  video: Video;
  showSpeedOptions?: boolean;
}> = ({ title, cover, video }) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const [showCover, setShowCover] = useState(true);
  const [hasIncrementedView, setHasIncrementedView] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [completionTimer, setCompletionTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  const videoRef = useRef<HTMLVideoElement>(null);

  // Check if we should render YouTube player
  const hasStandardVideo =
    video.versions?.[0]?.mp4Url || video.versions?.[0]?.webmUrl;
  const hasYouTubeVideo = video.versions?.[0]?.youtubeURL && !hasStandardVideo;

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    // Handle standard YouTube watch URLs like: https://www.youtube.com/watch?v=8Nn5uqE3C9w
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) {
      return watchMatch[1];
    }

    // Handle youtu.be short URLs like: https://youtu.be/8Nn5uqE3C9w
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) {
      return shortMatch[1];
    }

    // Handle embed URLs like: https://www.youtube.com/embed/8Nn5uqE3C9w
    const embedMatch = url.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) {
      return embedMatch[1];
    }

    return null;
  };

  // When the video is changed, show the cover again and reset the video
  useEffect(() => {
    setShowCover(true);
    setHasIncrementedView(false);
    determineBookmarkStatus();
    if (videoRef.current) {
      videoRef.current.load(); // This forces the video to reload with new sources
      videoRef.current.pause();
    }
  }, [video]);

  // Handle YouTube video tracking when component mounts or video changes
  useEffect(() => {
    // Clear any existing timer
    if (completionTimer) {
      clearTimeout(completionTimer);
      setCompletionTimer(null);
    }

    if (hasYouTubeVideo) {
      // Mark as started immediately
      handlePlay();

      // Start 5-minute completion timer
      const timer = setTimeout(
        () => {
          updateVideoScheduling(video.id, WatchState.COMPLETED);
        },
        5 * 60 * 1000
      ); // 5 minutes

      setCompletionTimer(timer);
    }

    // Cleanup function
    return () => {
      if (completionTimer) {
        clearTimeout(completionTimer);
      }
    };
  }, [hasYouTubeVideo, video.id]);

  // Clean up timer when component unmounts
  useEffect(() => {
    return () => {
      if (completionTimer) {
        clearTimeout(completionTimer);
      }
    };
  }, []);

  const handlePlay = async () => {
    if (!hasIncrementedView) {
      try {
        await incrementVideoView(video.id);
        setHasIncrementedView(true);
        posthog?.capture('video_viewed', {
          video_id: video.id,
          video_title: video.title,
        });
        if (video.watchState === WatchState.NOT_STARTED) {
          updateVideoScheduling(video.id, WatchState.IN_PROGRESS);
        }
      } catch (error) {
        console.error('Failed to increment video view:', error);
      }
    }
  };

  const determineBookmarkStatus = async () => {
    if (currentUser) {
      const response = await isVideoBookmarked(currentUser.id, video.id);
      if (response) {
        setIsBookmarked(response.isBookmarked);
      }
    }
  };

  const handleAddBookmark = async (articleId: string) => {
    if (currentUser) {
      await addVideoBookmark(currentUser.id, articleId);
      setIsBookmarked(true);
    }
    enqueueSnackbar('Bookmark added.', {
      autoHideDuration: 3000,
    });
  };

  const handleRemoveBookmark = async (articleId: string) => {
    if (currentUser) {
      await removeVideoBookmark(currentUser?.id, articleId);
      setIsBookmarked(false);
    }
    enqueueSnackbar('Bookmark removed.', {
      autoHideDuration: 3000,
    });
  };

  const handleEnded = () => {
    updateVideoScheduling(video.id, WatchState.COMPLETED);
  };

  const handleCoverClick = () => {
    setShowCover(false);
    if (hasYouTubeVideo) {
      handlePlay(); // For YouTube videos, we'll track the play immediately
    } else if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <div className="video-player">
      <div className="video-player_video">
        {showCover && !hasYouTubeVideo && (
          <div className="video-player_video_cover" onClick={handleCoverClick}>
            <div className="title">{title}</div>
            <div className="play-button">
              <Play />
            </div>
            <img src={cover} alt={title} />
          </div>
        )}
        {hasYouTubeVideo ? (
          <iframe
            width="100%"
            height="auto"
            style={{
              maxWidth: '100%',
              borderRadius: '.5rem',
              aspectRatio: '16 / 9',
              minHeight: '400px',
            }}
            src={`https://www.youtube.com/embed/${getYouTubeVideoId(video.versions[0].youtubeURL!)}`}
            title={video.title || 'YouTube video player'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          // Render standard video element
          <video
            ref={videoRef}
            width="100%"
            height="auto"
            style={{ maxWidth: '100%', borderRadius: '.5rem' }}
            controls
            poster={video.thumbnailUrl || undefined}
            onPlay={handlePlay}
            onEnded={handleEnded}
          >
            {video.versions?.[0]?.mp4Url && (
              <source src={video.versions[0].mp4Url} type="video/mp4" />
            )}
            {video.versions?.[0]?.webmUrl && (
              <source src={video.versions[0].webmUrl} type="video/webm" />
            )}
            Your browser does not support the video tag.
          </video>
        )}
      </div>
      <div className="video-player_info">
        <div className="video-player_info_left">
          {video.title && (
            <h3 className="video-player_info_left_title">{video.title}</h3>
          )}
          {video.description && (
            <div className="video-player_info_left_description">
              <p>{video.description}</p>
            </div>
          )}
          {video.citationAuthor && (
            <div className="caption-credit m-t-0-75">
              <div className="credit">
                <a
                  href={video.citationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {video.citationAuthor}
                </a>{' '}
                |{' '}
                <a
                  href="https://creativecommons.org/licenses/by-sa/4.0/deed.en"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {video.citationLicense}
                </a>
              </div>
            </div>
          )}
        </div>
        <div className="video-player_info_right">
          {currentUser && (
            <Tooltip
              title={isBookmarked ? 'Remove Bookmark' : 'Add Bookmark'}
              enterDelay={200}
              enterTouchDelay={0}
            >
              <button
                className={`button button--secondary button--bookmark ${
                  isBookmarked
                    ? 'button--bookmark--remove'
                    : 'button--bookmark--add'
                }`}
                aria-label={isBookmarked ? 'Remove Bookmark' : 'Add Bookmark'}
                style={{ marginLeft: 'auto' }}
                tabIndex={0}
                onClick={() => {
                  hapticsImpactLight();
                  if (isBookmarked) {
                    handleRemoveBookmark(video.id);
                  } else {
                    handleAddBookmark(video.id);
                  }
                }}
              >
                <BookmarkIcon />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
